import React, { useState, useEffect } from 'react';
import { wishlistAPI } from '../api';

function WishList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ name: '', url: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = async () => {
    try {
      const response = await wishlistAPI.getMyWishlist();
      setItems(response.data.items);
      setLoading(false);
    } catch (err) {
      setError('Failed to load wish list');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (editingItem) {
        await wishlistAPI.updateItem(editingItem.id, formData.name, formData.url);
      } else {
        await wishlistAPI.addItem(formData.name, formData.url);
      }

      setFormData({ name: '', url: '' });
      setEditingItem(null);
      await loadWishlist();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, url: item.url || '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setFormData({ name: '', url: '' });
    setError('');
  };

  const handleDelete = async (itemId) => {
    try {
      await wishlistAPI.deleteItem(itemId);
      await loadWishlist();
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const isAtLimit = items.length >= 10 && !editingItem;

  return (
    <div className="container">
      <div className="card">
        <h2>🎁 My Wish List</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Add items you'd like to receive. Your Secret Santa will be able to see this list!
        </p>
        <p style={{ color: '#666', marginBottom: '2rem', fontSize: '0.9rem' }}>
          You can add up to 10 items ({items.length}/10)
        </p>

        {error && <div className="error-message">{error}</div>}

        {isAtLimit && (
          <div className="info-message" style={{ background: '#fff3cd', color: '#856404', marginBottom: '1rem' }}>
            You've reached the maximum of 10 items. Delete an item to add a new one.
          </div>
        )}

        <form onSubmit={handleSubmit} className="wishlist-form">
          <div className="form-group">
            <label htmlFor="name">Item Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Cozy blanket, Book, Gadget"
              required
              disabled={isAtLimit}
            />
          </div>

          <div className="form-group">
            <label htmlFor="url">Link (Optional)</label>
            <input
              type="url"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="https://example.com/product"
              disabled={isAtLimit}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || isAtLimit}
            >
              {submitting ? 'Saving...' : editingItem ? 'Update Item' : 'Add Item'}
            </button>

            {editingItem && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        <div className="wishlist-items">
          <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>
            My Items ({items.length})
          </h3>

          {items.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
              No items yet. Add your first wish list item above!
            </p>
          ) : (
            <div className="wishlist-grid">
              {items.map((item) => (
                <div key={item.id} className="wishlist-item-card">
                  <div className="wishlist-item-content">
                    <h4>{item.name}</h4>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wishlist-link"
                      >
                        🔗 View Link
                      </a>
                    )}
                  </div>
                  <div className="wishlist-item-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleEdit(item)}
                      title="Edit item"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(item.id)}
                      title="Delete item"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WishList;
